import {
	isStoreAssistant,
	isStoreHuman,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';

/**
 * Maps each human message id to the Identity account id of the agent that
 * responded to it, paired by document order. Sent agent mention nodes use the
 * same identity so inactive mentions can be greyed out.
 *
 * When `enabled` is false the greying feature is off and an empty map is
 * returned, so every human message resolves to `undefined` and mention styling
 * stays on the baseline path. Gating here (the writer) keeps the whole feature
 * behind the flag, not just the render path.
 */
export const buildRespondedAgentIdByHumanMessageId = (
	conversationMessages: StoreChatItem[],
	enabled: boolean,
): Map<string, string> => {
	const map = new Map<string, string>();

	if (!enabled) {
		return map;
	}

	let pendingHumanId: string | undefined;
	for (const message of conversationMessages) {
		if (isStoreHuman(message)) {
			pendingHumanId = String(message.content.id);
		} else if (isStoreAssistant(message) && pendingHumanId) {
			const author = message.content.message.message.author;
			const respondedAgentId =
				normalizeAccountIdFromIdentityAri(author?.identity_account_id) ??
				normalizeAccountIdFromIdentityAri(author?.ari);
			if (respondedAgentId) {
				map.set(pendingHumanId, respondedAgentId);
			}
			pendingHumanId = undefined;
		}
	}

	return map;
};
